import { profileData } from "apis/profileData";
import { CancelSubscriptionConfirmation } from "components/CancelSubscriptionConfirmation";
import { PaperPlaneSkyblue } from "components/icons/PaperPlaneSkyblue";
import { RocketPurpleAnimate } from "components/icons/RocketPurpleAnimate";
import { PlatformSubscriptionCancelModal } from "components/PlatformSubscriptionCancelModal";
import { PlanCard } from "components/SubscriptionModal/PlanCard";
import dayjs from "dayjs";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import authContext from "jwt_context&axios/authContext";
import { useContext } from "react";
import { useSelector } from "react-redux";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { RootState } from "store/store";
import { SubscriptionInProfile } from "types/Models/subscription";
import { BackendError } from "types/utils/error";

type TaxSubscriptionModalContentProps = {
  price?: number;
  type?: string;
  features?: {
    description: string;
    active: boolean;
  }[];
  description?: string;
  onUpgrade?: () => void;
  hideUpgrade?: boolean;
  containsRadio?: boolean;
  isRadioDisabled?: boolean;
  onRadioChange?: (value: string) => void;
  radioValue?: string;
  isFreePlanActive?: boolean;
  disableFreeplanRadio?: boolean;
  isAddToCartFlow?: boolean;
  handleAddToCart?: () => void;
  isLoading?: boolean;
};

export const TaxSubscriptionsListContent = ({
  price = 0,
  type = "Free",
  features,
  description,
  onUpgrade,
  hideUpgrade,
  containsRadio,
  isRadioDisabled,
  onRadioChange,
  radioValue,
  isFreePlanActive,
  disableFreeplanRadio,
  isAddToCartFlow,
  handleAddToCart,
  isLoading,
}: TaxSubscriptionModalContentProps) => {
  const { authtoken, updateUser } = useContext(authContext);
  const { platform_subscription: groupPlatformSubscription } =
    useCurrentGroupContext();
  const cancelationModal = useModal();
  const { alertToast } = useToast();
  const { credits } = useSelector((state: RootState) => state.credits);

  let {
    platform_subscription: currentSubscription,
  }: {
    platform_subscription: SubscriptionInProfile;
  } = authtoken || {};

  if (!Boolean(currentSubscription.subscription_name)) {
    currentSubscription = groupPlatformSubscription;
  }

  const isCurrentYearIncorporated =
    dayjs(currentSubscription.scheduled_date)
      .subtract(1, "year")
      .year()
      .toString() === new Date().getFullYear().toString();

  const freePlanFeatures = [
    {
      description: "Deadlines & Reminders",
      active: true,
    },
    {
      description: "Includes 3 user seats",
      active: true,
    },
    {
      description: "Access to all tax & compliance filings",
      active: false,
    },
    {
      description: "Dedicated support on chat",
      active: false,
    },
  ];

  const handleCancelSubscription = async () => {
    try {
      cancelationModal.close();
      const profile = await profileData();
      updateUser(profile);
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <section className="t-flex t-gap-4">
      <PlanCard
        price={0}
        type="Free"
        buttonText="Subscribe"
        features={freePlanFeatures}
        icon={<PaperPlaneSkyblue />}
        description="Check deadlines and set reminders"
        isButtonDisabled
        hideUpgrade={hideUpgrade}
        containsRadio={containsRadio}
        isRadioDisabled={isRadioDisabled || disableFreeplanRadio}
        onRadioChange={onRadioChange}
        radioValue={radioValue}
        isActive={isFreePlanActive}
        onDowngrade={cancelationModal.open}
      />
      <PlanCard
        price={price}
        billingCycle="year"
        type={type}
        buttonText="Upgrade to Standard"
        features={features || []}
        icon={<RocketPurpleAnimate />}
        description={description || ""}
        onUpgrade={onUpgrade}
        hideUpgrade={hideUpgrade}
        containsRadio={containsRadio}
        isRadioDisabled={isRadioDisabled}
        onRadioChange={onRadioChange}
        radioValue={radioValue}
        isActive={disableFreeplanRadio}
        isAddToCartFlow={isAddToCartFlow}
        handleAddToCart={handleAddToCart}
        isLoading={isLoading}
      />
      {currentSubscription && (
        <PlatformSubscriptionCancelModal
          isOpen={cancelationModal.isOpen}
          onClose={cancelationModal.close}
          AccessPoints={[
            Boolean(credits) && (
              <>
                You will lose <b>{credits} Inkle Credits</b>
              </>
            ),
            "Chat history & any pending or upcoming tasks",
            "Deals on softwares & introductions through Inkle Perks",
            "Access to Deadline alerts & compliance health checks",
          ]}
          planCancelLabel="Are you sure you want to cancel Inkle Tax Standard Plan and
              downgrade to free plan?"
          dataExportable
          handleCancelSubscription={handleCancelSubscription}
          subscription={currentSubscription}
          isCurrentYearIncorporated={isCurrentYearIncorporated}
          groupSubscriptionId={currentSubscription.uuid}
        />
      )}
    </section>
  );
};
